import React from 'react'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import clsx from 'clsx'

import Grid from '@material-ui/core/Grid'
import Toolbar, { ToolbarProps } from '@material-ui/core/Toolbar'
import { withStyles, WithStyles, createStyles, Theme, Chip } from '@material-ui/core'

import T from 'components/FormattedMessage'
import Button from 'components/Button'
import AppLogo from 'components/AppLogo'
import LinkIconButton from 'components/LinkIconButton'
import AccountInfo from 'components/AccountInfo'
import { UserInfoResponse } from 'protos'
import { MessageKey } from 'types'

type AccountInfoProps = React.ComponentProps<typeof AccountInfo>

interface Props extends ToolbarProps {
  accountInfo?: AccountInfoProps
  counters?: UserInfoResponse.ICounters | null
}

const styles = (theme: Theme) => createStyles({
  button: {
    color: '#fff',
    paddingLeft: theme.spacing() * 2,
    paddingRight: theme.spacing() * 2,
  },
  active: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.accent.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  chip: {
    height: 20,
    marginLeft: theme.spacing(),
    fontSize: theme.typography.caption.fontSize,
  },
  chipInactive: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  chipActive: {
    backgroundColor: theme.palette.accent.main,
    color: theme.palette.accent.contrastText,
  },
})

const buttons: Array<{
  key: MessageKey,
  href: string,
  getBadgeContent?: (counters?: UserInfoResponse.ICounters | null) => number,
}> = [
    {
      key: 'centerbutton_games',
      href: '/games',
    },
    {
      key: 'root_tab_leaderboard',
      href: '/ranking',
    },
    {
      key: 'root_tab_contests',
      href: '/events',
      getBadgeContent: counters => (counters?.liveContests ?? 0) + (counters?.pendingFinishedContests ?? 0),
    },
    {
      key: 'root_tab_friends',
      href: '/friends',
      getBadgeContent: counters => counters?.pendingFriendRequests ?? 0,
    },
    {
      key: 'root_tab_account',
      href: '/profile',
    },
  ]

type InjectedProps = Props & WithStyles<typeof styles> & RouteComponentProps<{}>

const ToolbarDesktop: React.SFC<InjectedProps> = ({
  accountInfo,
  counters,
  classes,
  location,
  ...props
}) => (
    <Toolbar color="accent" {...props}>
      <LinkIconButton to="/games">
        <AppLogo color="primary" />
      </LinkIconButton>

      <Grid direction="row" justify="space-evenly" alignItems="center" container>
        {
          buttons.map((button) => {
            const isActive = location.pathname.startsWith(button.href.toString())
            const badgeContent = button.getBadgeContent?.(counters)

            return (
              <Grid key={button.key} item>
                <Button to={button.href} color="inherit" className={classes.button} activeClassName={classes.active}>
                  <T id={button.key} />
                  {!!badgeContent && (
                    <Chip
                      className={clsx(classes.chip, {
                        [classes.chipInactive]: !isActive,
                        [classes.chipActive]: isActive,
                      })}
                      label={badgeContent}
                      color="inherit"
                    />
                  )}
                </Button>
              </Grid>
            )
          })
        }
      </Grid>

      <AccountInfo {...accountInfo} />
    </Toolbar>
  )

export default withStyles(styles)(withRouter(ToolbarDesktop))
